import { Box, Button, Stack, TablePagination, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import FilterIcon from "src/assets/svg-icons/aggregators/filter.svg";
import DownloadIcon from "src/assets/svg-icons/download.svg";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import GeneralLedgerTable from "./table";
import { downloadAsXLS, oneBranchHasPermission } from "src/utils";
import DateRangeInput from "src/shared/components/date-range";
import moment from "moment";
import { PurchasingPermissions } from "src/constants/permissions";
import {
  useGetGeneralLedgerList,
  useDownloadGeneralLedgers,
} from "src/api/tree/general-ledger";
import FilterDrawer from "./filters";
import { TreeNode } from "src/shared/components/account-tree-dropdown";

const GeneralLedger = () => {
  const [searchParams] = useSearchParams();
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [total, setTotal] = useState<number>(0);
  const { t } = useTranslation("purchasing");
  const { t: generalT } = useTranslation("general");
  const [dateRange, setDateRange] = useState<{
    startDate: string;
    endDate: string;
  }>({
    startDate: "",
    endDate: "",
  });

  const [openFilter, setOpenFilter] = useState(false);
  const [journalId, setJournalId] = useState("");
  const [source, setSource] = useState("");
  const [reference, setReference] = useState(searchParams.get("reference") || "");
  const [accountNode, setAccountNode] = useState<TreeNode | null>(null);
  const [branchId, setBranchId] = useState("");
 
  const filters = {
    start_date: dateRange.startDate || undefined,
    end_date: dateRange.endDate || undefined,
    ...(!!journalId && { journal_id: journalId }),
    ...(!!source && { source }),
    ...(!!reference && { reference_number: reference }),
    ...(!!accountNode && { account_id: String(accountNode.id) }),
    ...(!!branchId && { branch_id: branchId }),
  };

  const { data, refetch, isFetching } = useGetGeneralLedgerList({
    page: pageNumber + 1,
    ...filters,
  });

  const {
    data: downloadedData,
    refetch: refetchDownload,
    isFetching: isFetchingDownload,
    status: downloadStatus,
  } = useDownloadGeneralLedgers({
    ...filters,
  });

  const addEventPermission = oneBranchHasPermission(
    PurchasingPermissions.addEvent,
  );

  const handleChangePage = (event: unknown, newPage: number) => {
    setPageNumber(newPage);
  };

  const handleDownload = () => {
    refetchDownload();
  };

  const handleOpenFilter = () => {
    setOpenFilter(true);
  };

  const handleCloseFilter = () => {
    setOpenFilter(false);
  };

  useEffect(() => {
    setPageNumber(0);
  }, [journalId, source, reference, accountNode, branchId, dateRange]);

  useEffect(() => {
    if (data) {
      setTotal(data.meta.total);
    }
  }, [data]);

  useEffect(() => {
    if (downloadStatus === "success" && !!downloadedData) {
      const utf8CSVData = `\uFEFF${downloadedData}`;
      downloadAsXLS(utf8CSVData, "general-ledger");
    }
  }, [isFetchingDownload]);

  return (
    <Stack spacing={3} maxWidth={{ xs: "100%", md: `calc(100vw - 316px)` }}>
      <Stack
        sx={{ flexDirection: { xs: "column", md: "row" } }}
        justifyContent={"space-between"}
        gap={2}
        alignItems={"center"}
      >
        <Typography
          fontWeight={600}
          lineHeight={"19.2px"}
          color={"#475467"}
          textTransform={"uppercase"}
        >
          {t("journalEntries")}
        </Typography>
        <Stack direction={"row"} gap={2} flexWrap={"wrap"}>
          <Box sx={{ minWidth: 207 }}>
            <DateRangeInput
              startDate={
                dateRange.startDate ? moment(dateRange.startDate) : null
              }
              startDateId={`your_unique_start_date_id`}
              endDate={dateRange.endDate ? moment(dateRange.endDate) : null}
              endDateId={`your_unique_end_date_id`}
              onDatesChange={(arg: {
                startDate: moment.Moment | null;
                endDate: moment.Moment | null;
              }) => {
                setDateRange({
                  startDate: arg.startDate?.format("yyyy-MM-DD") as string,
                  endDate: arg.endDate?.format("yyyy-MM-DD") as string,
                });
              }}
              showClearDates
            />
          </Box>
          <LoadingButton
            color="tertiary"
            variant="outlined"
            endIcon={<img src={DownloadIcon} alt="" />}
            loading={isFetchingDownload}
            onClick={handleDownload}
          >
            {generalT("download")}
          </LoadingButton>
          <Button
            color="tertiary"
            variant="outlined"
            startIcon={<img src={FilterIcon} alt="" />}
            onClick={handleOpenFilter}
          >
            {t("filter")}
          </Button>
        </Stack>
      </Stack>
      <GeneralLedgerTable data={data?.data} isFetching={isFetching} />
      <TablePagination
        rowsPerPageOptions={[20]}
        component="div"
        count={total}
        rowsPerPage={20}
        page={pageNumber}
        onPageChange={handleChangePage}
        showFirstButton
        showLastButton
      />

      {openFilter && (
        <FilterDrawer
          open={openFilter}
          handleClose={handleCloseFilter}
          journalId={journalId}
          setJournalId={setJournalId}
          source={source}
          setSource={setSource}
          reference={reference}
          setReference={setReference}
          accountNode={accountNode}
          setAccountNode={setAccountNode}
          branchId={branchId}
          setBranchId={setBranchId}
        />
      )}
    </Stack>
  );
};

export default GeneralLedger;
